// src/components/routing/TeamManagementRoute.jsx

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTeamPermission } from '../../hooks/useTeamPermission';
import { PageLoading } from '../common/LoadingSpinner';
import { ROUTES, USER_TYPES } from '../../constants/constants';

/**
 * TeamManagementRoute
 *
 * Props:
 *   requireManageUsers  (bool) — needs manage_users role perm (client owner always passes)
 *   requireClientOwner  (bool) — needs userType === CLIENT (blocks child users entirely)
 *
 * Access matrix:
 *   No Team_Management_Permission     → /403
 *   Child user, no manage_users       → /403
 *   Child user, has manage_users,
 *     requireClientOwner = true       → /403  ← NEW: blocks Dashboard/Roles/Teams/Hierarchy
 *   Child user, has manage_users,
 *     requireClientOwner = false      → ✅ allowed (Team Overview, Add/Edit Users)
 *   CLIENT owner                      → ✅ always allowed (owns the license)
 */
const TeamManagementRoute = ({
  children,
  requireManageUsers = false,
  requireClientOwner = false,   // NEW PROP
}) => {
  const { isAuthenticated, isLoading: authLoading, userType } = useAuth();
  const {
    hasTeamManagementAccess,
    perms,
    loading: permsLoading,
    permsFetchFailed,
    retryPerms,
  } = useTeamPermission();

  if (authLoading || permsLoading) {
    return <PageLoading message="Checking permissions..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (permsFetchFailed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4">
        <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Could not verify permissions</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
          There was a problem loading your access permissions. Please try again.
        </p>
        {typeof retryPerms === 'function' && (
          <button
            onClick={retryPerms}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const isClientOwner = userType === USER_TYPES.CLIENT;

  // Must have Team_Management_Permission on the license
  if (!hasTeamManagementAccess) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // Child users must additionally have manage_users role perm
  if (!isClientOwner && !perms?.manage_users) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // Pages that are CLIENT OWNER ONLY (Dashboard, Roles, Teams, Hierarchy)
  // Child users with manage_users are still blocked here
  if (requireClientOwner && !isClientOwner) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  return children;
};

export default TeamManagementRoute;